/**
 * /settings/currency — Multi-currency setup.
 *
 * For most customers (single-currency operations) this stays off and
 * the CCY columns stay hidden across grids (Invoices, Payments, POs,
 * Parties). Customers operating across borders flip the toggle here,
 * pick the foreign currencies they transact in, and the CCY column
 * surfaces. FX rate management lives at /fx-rates.
 *
 * Storage: persistence is currently localStorage (per browser, per
 * entity). Server-side persistence is a planned migration that would
 * add `entities.multi_currency_enabled` + `entities.enabled_currency_codes`
 * columns. Until then, this page configures display preferences only —
 * underlying transactions can still be in any currency.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "@tanstack/react-router";

import { myEntitiesQueryOptions } from "@/lib/queries";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/settings/currency")({
  component: CurrencySetupPage,
});

// ── ISO 4217 starter list (most-used in construction supply chains) ─────────
// Not a complete catalog — the Currency table in the backend has the full
// 180-currency list. This is the picker shortlist for common cases.
interface CurrencyMeta {
  code: string;
  name: string;
  symbol: string;
}

const COMMON_CURRENCIES: CurrencyMeta[] = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "$" },
  { code: "MXN", name: "Mexican Peso", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "AUD", name: "Australian Dollar", symbol: "$" },
  { code: "CHF", name: "Swiss Franc", symbol: "Fr" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$" },
  { code: "ZAR", name: "South African Rand", symbol: "R" },
];

const lsKey = (entityId: string, suffix: string) =>
  `bynaus.entity.${entityId}.${suffix}`;

function readMultiCurrencyEnabled(entityId: string): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(lsKey(entityId, "multi_currency_enabled")) === "true";
}

function readEnabledCurrencies(entityId: string): string[] {
  if (typeof window === "undefined") return [];
  const raw = window.localStorage.getItem(lsKey(entityId, "enabled_currencies"));
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function CurrencySetupPage() {
  const entitiesQ = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQ.data?.find((e) => e.is_current);
  const entityId = currentEntity?.id ?? "";
  const baseCurrency = (currentEntity as { base_currency_code?: string } | undefined)
    ?.base_currency_code ?? "USD";

  const [enabled, setEnabled] = useState(false);
  const [foreignCodes, setForeignCodes] = useState<string[]>([]);
  const [pickerCode, setPickerCode] = useState("");

  // Hydrate from localStorage when the entity resolves
  useEffect(() => {
    if (!entityId) return;
    setEnabled(readMultiCurrencyEnabled(entityId));
    setForeignCodes(readEnabledCurrencies(entityId));
  }, [entityId]);

  function persist(nextEnabled: boolean, nextForeign: string[]) {
    if (!entityId) return;
    window.localStorage.setItem(lsKey(entityId, "multi_currency_enabled"), String(nextEnabled));
    window.localStorage.setItem(lsKey(entityId, "enabled_currencies"), JSON.stringify(nextForeign));
  }

  function toggleEnabled(next: boolean) {
    setEnabled(next);
    persist(next, foreignCodes);
  }

  function addForeign() {
    if (!pickerCode || pickerCode === baseCurrency) return;
    if (foreignCodes.includes(pickerCode)) return;
    const next = [...foreignCodes, pickerCode];
    setForeignCodes(next);
    setPickerCode("");
    persist(enabled, next);
  }

  function removeForeign(code: string) {
    const next = foreignCodes.filter((c) => c !== code);
    setForeignCodes(next);
    persist(enabled, next);
  }

  // The picker list excludes the base currency and any already-added
  const pickerOptions = useMemo(
    () =>
      COMMON_CURRENCIES.filter(
        (c) => c.code !== baseCurrency && !foreignCodes.includes(c.code),
      ),
    [baseCurrency, foreignCodes],
  );

  return (
    <Box sx={{ p: 4, maxWidth: 900, mx: "auto" }}>
      <Stack direction="row" alignItems="center" gap={2} mb={1}>
        <CurrencyExchangeIcon sx={{ fontSize: 28, color: "#3b82f6" }} />
        <Typography variant="h5" fontWeight={600}>
          Multi-currency setup
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <HelpHint docPath="multi-currency/overview.md" label="Multi-currency help" />
        </Box>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Most customers operate in a single currency. If you transact in multiple
        currencies, enable it here and choose which ones — the <code>CCY</code>{" "}
        column will appear across the Bills, Invoices, Payments, POs, and
        Parties grids automatically.
      </Typography>

      {/* ── Step 1: Master toggle ────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              1. Enable multi-currency
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              When off, all currency-related UI is hidden — every amount is
              assumed to be your base currency. Your existing transactions are
              not affected; this controls UX visibility.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Switch
              checked={enabled}
              onChange={(e) => toggleEnabled(e.target.checked)}
              disabled={!entityId}
            />
            <Chip
              label={enabled ? "ON" : "OFF"}
              size="small"
              color={enabled ? "primary" : "default"}
              variant={enabled ? "filled" : "outlined"}
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* ── Step 2: Base currency (read-only) ────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, opacity: enabled ? 1 : 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          2. Base currency
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your books are kept in this currency. The GL, financial statements,
          and reports all post in the base currency. To change it, edit the
          entity in <Link to="/settings/entities" style={{ color: "var(--bynaus, #1B4FD8)" }}>
            Setup → Entities
          </Link>{" "}
          (changing the base currency on an entity with posted transactions is
          a major operation — contact support first).
        </Typography>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderRadius: 1,
            bgcolor: "action.hover",
            fontFamily: "monospace",
          }}
        >
          <Typography variant="h6" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
            {baseCurrency}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {COMMON_CURRENCIES.find((c) => c.code === baseCurrency)?.name ?? "Base currency"}
          </Typography>
        </Box>
      </Paper>

      {/* ── Step 3: Foreign currencies ───────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, opacity: enabled ? 1 : 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          3. Foreign currencies you transact in
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These appear in currency dropdowns when entering bills, invoices,
          POs, and payments. The base currency ({baseCurrency}) is always
          included automatically.
        </Typography>
        <Stack direction="row" gap={1.5} alignItems="center" sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 280 }} disabled={!enabled}>
            <InputLabel>Add a currency</InputLabel>
            <Select
              value={pickerCode}
              label="Add a currency"
              onChange={(e) => setPickerCode(e.target.value)}
            >
              {pickerOptions.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  <span style={{ fontFamily: "monospace", marginRight: 8 }}>{c.code}</span>
                  <span style={{ color: "#6B7280" }}>{c.name}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={addForeign}
            disabled={!enabled || !pickerCode}
            size="small"
          >
            Add
          </Button>
        </Stack>
        {foreignCodes.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            No foreign currencies enabled yet. Add one above when you're ready.
          </Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {foreignCodes.map((code) => {
                const meta = COMMON_CURRENCIES.find((c) => c.code === code);
                return (
                  <TableRow key={code} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {code}
                    </TableCell>
                    <TableCell>{meta?.name ?? "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{meta?.symbol ?? "—"}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => removeForeign(code)} disabled={!enabled}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ── Step 4: FX rates ─────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, opacity: enabled ? 1 : 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
          4. FX rate source
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each foreign-currency transaction is recorded at the rate on its
          transaction date. Period-end revaluation re-marks open foreign-
          currency balances at the closing rate; the gain/loss posts to your
          FX gain/loss account. FX rates are managed at the dedicated rates
          page.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to="/fx-rates"
          endIcon={<OpenInNewIcon />}
          disabled={!enabled}
        >
          Open FX Rates
        </Button>
      </Paper>

      {/* ── Step 5: What changes ─────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          What changes when multi-currency is on
        </Typography>
        <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, fontSize: 13, lineHeight: 1.6 } }}>
          <li>
            The <code>CCY</code> column appears on the Bills, Invoices, Payments,
            POs, and Parties grids by default. (Power users can still toggle it
            off per grid via the toolbar's Columns menu.)
          </li>
          <li>
            Bill and invoice forms gain a currency picker (only the currencies
            you enabled above appear in the dropdown).
          </li>
          <li>
            Period-close gains an FX revaluation step — open foreign-currency
            balances are re-marked to the closing rate before the period locks.
          </li>
          <li>
            Reports and financial statements continue to display in your base
            currency ({baseCurrency}) — foreign amounts are translated using
            the FX rate on the transaction date.
          </li>
          <li>
            The Currency Code column on every export (CSV / PDF) becomes
            populated with the original transaction currency rather than blank.
          </li>
        </Box>
      </Paper>

      {!entityId && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          Could not resolve the current entity. The multi-currency setting is
          per-entity; switch to a specific entity from the entity picker in the
          top nav and reload this page.
        </Alert>
      )}
    </Box>
  );
}
